/**
 * Storage key categorization for organized clearing and diagnostics
 *
 * PRINCIPLE: Categories represent USER INTENT, not storage mechanism
 * Each category can have keys in both localStorage AND sessionStorage
 */

import { type StorageCategory } from './types';

/**
 * Storage categories organized by functionality
 * Users think "clear my recording" not "clear localStorage.recording"
 */
export const STORAGE_CATEGORIES: Record<string, StorageCategory> = {
  /**
   * Recording-related state (cross-tab coordination)
   * Clearing fixes: Recording stuck, frozen, or in invalid state
   * Warning: Loses current recording progress
   */
  recording: {
    localStorage: [
      'recordingStatus',
      'currentRecordingSessionId',
      'transcriptionSessionId',
      'elapsedTime',
      'recording_starter_id', // Tab ownership tracking
      'noteInformation',
      'useSharedMedia',
      'noAudioStatus',
      'microphonePermissionGranted',
      'selectedMicrophoneId',
      'selectedMicrophone',
      'availableMicrophones',
      'appointmentType',
      'isLeaving',
    ],
    sessionStorage: [
      'tabId', // Recording tab coordination (works with recording_starter_id)
    ],
  },

  /**
   * Encounter-related state
   * Clearing fixes: Encounter not loading, stuck on wrong patient
   */
  encounter: {
    localStorage: ['activeEncounterId'],
    sessionStorage: [],
  },

  /**
   * Authentication and user session
   * Clearing these will log user out
   */
  auth: {
    localStorage: [
      'accessToken',
      'userInfo',
      'lastUserLogin',
      'returnTo',
      'urlParams',
      'invite_token',
      'referralCode',
    ],
    sessionStorage: [
      'auth_callback_login_attempted', // Auth callback loop prevention
      'auth_refresh_count', // Auth refresh attempt tracking
    ],
  },

  /**
   * Module customization workflow state
   * Clearing resets in-progress customization
   */
  moduleCustomization: {
    localStorage: [],
    sessionStorage: [
      'edit_instructions',
      'selected_ai_option',
      'module_customization_session_id',
      'module_customization_session_step',
    ],
  },

  /**
   * OAuth integration state
   * Clearing may require re-authentication with third parties
   */
  oauth: {
    localStorage: ['google_oauth_state'],
    sessionStorage: ['google_oauth_params'],
  },

  /**
   * Marketing attribution (UTM parameters)
   * Clearing loses attribution tracking for current session
   */
  marketing: {
    localStorage: [],
    sessionStorage: ['utm_session'],
  },

  /**
   * User preferences and settings
   * Clearing may inconvenience user but safe
   */
  preferences: {
    localStorage: [
      'rightSidebarDocked',
      'noteFilterSelection',
      'noteSortOrder',
      'noteItemsPerPage',
      'noteTitleOnly',
      'panelSize',
    ],
    sessionStorage: [],
  },

  /**
   * UI state and feature flags
   * Safe to clear, resets UI to default state
   */
  uiState: {
    localStorage: [
      'cardVisibilityFlags',
      'billing-collection-tooltip-seen',
      'addToHomeScreenPromptDismissed',
      'onboardingBannerShown',
      'viewed_chrome_extension_tooltips',
      'navScheduleBadgeClicked',
      'syncGoogleCalendarBadgeClicked',
      'templateBuilderBadgeClicked',
      'patientListClicked',
      'editPatientSectionsClicked',
    ],
    sessionStorage: [],
  },

  /**
   * Cache and temporary data
   * Safe to clear, may cause slight performance hit on next load
   */
  cache: {
    localStorage: [
      'dashboard_notes_cache',
      'sharedTemplateId',
      'sharedTemplateAdded',
      'template_builder_session_id',
    ],
    sessionStorage: [],
  },

  /**
   * Feature usage tracking
   * Safe to clear, only affects analytics
   */
  featureTracking: {
    localStorage: ['hasUsedAiInstruction', 'jotrx_setup_banner_dismissed'],
    sessionStorage: [],
  },
} as const;

/**
 * Pattern to match redux-persist keys
 * Format: persist:root, persist:feature_*
 */
export const REDUX_PERSIST_PATTERN = /^persist:(root|feature_)/;

/**
 * Get all storage keys in a specific category
 */
export function getKeysInCategory(category: keyof typeof STORAGE_CATEGORIES): {
  localStorage: readonly string[];
  sessionStorage: readonly string[];
} {
  return STORAGE_CATEGORIES[category];
}

/**
 * Get all localStorage keys across all categories
 */
export function getAllLocalStorageKeys(): string[] {
  return Object.values(STORAGE_CATEGORIES).flatMap(cat => [...cat.localStorage]);
}

/**
 * Get all sessionStorage keys across all categories
 */
export function getAllSessionStorageKeys(): string[] {
  return Object.values(STORAGE_CATEGORIES).flatMap(cat => [...cat.sessionStorage]);
}

/**
 * Get all non-critical keys (safe to clear without logout)
 */
export function getNonCriticalKeys(): { localStorage: string[]; sessionStorage: string[] } {
  const categories = [
    'recording',
    'encounter',
    'moduleCustomization',
    'oauth',
    'marketing',
    'preferences',
    'uiState',
    'cache',
    'featureTracking',
  ];

  return {
    localStorage: categories.flatMap(cat => [...STORAGE_CATEGORIES[cat].localStorage]),
    sessionStorage: categories.flatMap(cat => [...STORAGE_CATEGORIES[cat].sessionStorage]),
  };
}

/**
 * Check if a key is critical (should not be cleared without warning)
 */
export function isCriticalKey(
  key: string,
  storageType: 'localStorage' | 'sessionStorage',
): boolean {
  const authKeys =
    storageType === 'localStorage'
      ? STORAGE_CATEGORIES.auth.localStorage
      : STORAGE_CATEGORIES.auth.sessionStorage;

  return authKeys.includes(key);
}

/**
 * Check if a key is a redux-persist key
 */
export function isReduxPersistKey(key: string): boolean {
  return REDUX_PERSIST_PATTERN.test(key);
}

/**
 * Categorize a storage key
 */
export function categorizeKey(key: string, storageType: 'localStorage' | 'sessionStorage'): string {
  for (const [category, keys] of Object.entries(STORAGE_CATEGORIES)) {
    const keysForType = storageType === 'localStorage' ? keys.localStorage : keys.sessionStorage;
    if (keysForType.includes(key)) {
      return category;
    }
  }

  if (storageType === 'localStorage' && isReduxPersistKey(key)) {
    return 'reduxPersist';
  }

  return 'unknown';
}

/**
 * Get a friendly description for a category
 */
export function getCategoryDescription(category: string): string {
  const descriptions: Record<string, string> = {
    recording: 'Recording state and tab coordination',
    encounter: 'Active encounter tracking',
    auth: 'Authentication and user session (will log you out)',
    moduleCustomization: 'In-progress module customization',
    oauth: 'Third-party integration tokens',
    marketing: 'Marketing attribution (UTM parameters)',
    preferences: 'User preferences and settings',
    uiState: 'UI state and feature flags',
    cache: 'Cached data and temporary files',
    featureTracking: 'Feature usage analytics',
    reduxPersist: 'App state persistence',
  };

  return descriptions[category] || 'Unknown category';
}
